import { useState } from "react";
import { useTrackedLinks, trackedIntent, trackedCopyLink, trackedCaption, trackedEmailHref } from "@/lib/utm";
import { Copy, Check, Twitter, Facebook, MessageCircle, Linkedin, Instagram, Mail } from "lucide-react";
import { toast } from "sonner";

// Inline social share row for a public blog/reflection post — every outbound link is
// UTM-tagged (and upgraded to a Short.io-tracked link when the visitor is logged in).
export default function ShareBar({ url, hook, campaign = "share", testid = "share-bar" }) {
  const [copied, setCopied] = useState(false);
  const tracked = useTrackedLinks(url, campaign);
  const copyTarget = trackedCopyLink(tracked, url, campaign);
  const cta = "Read the full reflection:";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyTarget);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — select and copy the link manually");
    }
  };

  const copyInstagram = async () => {
    try {
      await navigator.clipboard.writeText(trackedCaption(tracked, url, hook, campaign, cta));
      toast.success("Caption + link copied — paste it into your Instagram post or story");
    } catch {
      toast.error("Couldn't copy the caption");
    }
  };

  const targets = [
    { id: "x", label: "Share on X", Icon: Twitter, href: trackedIntent("x", tracked, url, hook, campaign, cta) },
    { id: "facebook", label: "Share on Facebook", Icon: Facebook, href: trackedIntent("fb", tracked, url, hook, campaign, cta) },
    { id: "whatsapp", label: "Share on WhatsApp", Icon: MessageCircle, href: trackedIntent("wa", tracked, url, hook, campaign, cta) },
    { id: "linkedin", label: "Share on LinkedIn", Icon: Linkedin, href: trackedIntent("li", tracked, url, hook, campaign, cta) },
  ];

  return (
    <div data-testid={testid} className="flex items-center gap-2 flex-wrap">
      <span className="text-xs uppercase tracking-widest text-slate-500 mr-1">Share</span>
      {targets.map((t) => (
        <a key={t.id} data-testid={`${testid}-${t.id}`} href={t.href} target="_blank" rel="noreferrer"
           aria-label={t.label} title={t.label}
           className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#14293380] transition-colors">
          <t.Icon className="w-4 h-4" strokeWidth={1.5} />
        </a>
      ))}
      <button data-testid={`${testid}-instagram`} onClick={copyInstagram} aria-label="Copy caption for Instagram"
              title="Instagram has no direct web share — copy the caption + link to paste"
              className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#14293380] transition-colors">
        <Instagram className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <a data-testid={`${testid}-email`} href={trackedEmailHref(tracked, url, [hook, cta], "A reflection from OpenWord AI", campaign)}
         aria-label="Share by email" title="Share by email"
         className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#14293380] transition-colors">
        <Mail className="w-4 h-4" strokeWidth={1.5} />
      </a>
      <button data-testid={`${testid}-copy`} onClick={copy} aria-label="Copy link" title="Copy link"
              className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#14293380] transition-colors">
        {copied ? <Check className="w-4 h-4" strokeWidth={1.5} /> : <Copy className="w-4 h-4" strokeWidth={1.5} />}
      </button>
    </div>
  );
}
